import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import {
  AppSession,
  AddressData,
  VehicleData,
  createDefaultSession,
  MAX_ADDRESSES,
  MAX_VEHICLES,
} from "@/services/defaultSession";
import {
  getSession,
  setSession as writeSession,
  clearSession,
  isSessionValid,
} from "@/services/sessionStorage";
// cart_id is now managed by the backend via CartContext

/* ------------------------------------------------------------------ */
/*  Context value type                                                 */
/* ------------------------------------------------------------------ */

export interface SessionContextValue {
  session: AppSession;

  // Order actions
  setRestaurantId: (id: string) => void;
  setOrderType: (type: "pickup" | "delivery" | "dine_in", preserveCart?: boolean) => void;
  setBranch: (branchId: string, branchName: string, asapAllowed: number, scheduleAllowed: number, preserveCart?: boolean) => void;
  setPaymentMethod: (methodId: string, methodObject: Record<string, unknown>) => void;
  setPaymentGateway: (gateway: "myfatoorah" | "tap", method: string | null) => void;
  clearPayment: () => void;
  setSchedule: (day: string, period: string) => void;
  clearSchedule: () => void;
  setDeliveryTime: (time: string | null) => void;
  setDistance: (distance: number | null) => void;
  setVehicle: (vehicleData: VehicleData) => void;
  clearVehicle: () => void;
  toggleUsePoints: () => void;
  toggleUseWallet: () => void;
  setBranchStatus: (isOpen: boolean, isBusy: boolean, reason?: string | null) => void;

  // Customer actions
  loginCustomer: (token: string, data: Record<string, unknown>) => void;
  logoutCustomer: () => void;
  setGuestMode: () => void;
  updateCustomerData: (data: Record<string, unknown>) => void;
  setGeolocation: (lat: number, lng: number) => void;
  setLastCoordinates: (lat: number, lng: number) => void;
  setSelectedAddress: (address: AddressData) => void;
  addAddress: (address: AddressData) => void;
  updateAddress: (index: number, address: AddressData) => void;
  removeAddress: (index: number) => void;
  addVehicle: (vehicle: VehicleData) => void;
  removeVehicle: (index: number) => void;

  // Cart actions
  setCartId: (id: string) => void;
  clearCartId: () => void;
  setCartItemsLength: (length: number) => void;
  // Lifecycle
  resetSession: () => void;
  resetOrder: () => void;

  // Computed
  isGuest: boolean;
  isAuthenticated: boolean;
  orderType: string;
  branchName: string | null;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AppSession>(() => {
    const stored = getSession();
    return isSessionValid(stored) ? stored : createDefaultSession();
  });

  // Source tag for cartId debug logging
  const sourceRef = useRef<string>("debounced-write");

  // Debounced write to localStorage
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const src = sourceRef.current;
    timerRef.current = setTimeout(() => writeSession(session, src), 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [session]);

  // Cross-tab sync
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "blazebite_session" && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          // Log cross-tab cartId changes
          const prevCartId = session.order?.cartId ?? "";
          const nextCartId = parsed?.order?.cartId ?? "";
          if (prevCartId !== nextCartId) {
            console.warn(
              `[DEBUG cartId] CROSS-TAB | source=storage-event | "${prevCartId}" → "${nextCartId}" | ${new Date().toISOString()}`
            );
          }
          if (isSessionValid(parsed)) setSessionState(parsed);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [session.order?.cartId]);

  /* ---------- helpers ---------- */

  const update = useCallback(
    (fn: (prev: AppSession) => AppSession) => setSessionState((prev) => fn(JSON.parse(JSON.stringify(prev)))),
    []
  );

  const clearPaymentFields = (order: AppSession["order"]) => {
    order.paymentMethodId = null;
    order.paymentSession = null;
    order.paymentBrand = null;
    order.paymentMyfatoorah = false;
    order.paymentMyfatoorahMethod = null;
    order.paymentTap = false;
    order.paymentTapMethod = null;
    order.paymentMethodObject = {};
  };

  /* ---------- Order actions ---------- */

  const setRestaurantId = useCallback(
    (id: string) =>
      update((s) => {
        s.order.restaurantId = id;
        return s;
      }),
    [update]
  );

  const setOrderType = useCallback(
    (type: "pickup" | "delivery" | "dine_in", preserveCart?: boolean) => {
      sourceRef.current = `setOrderType(${type},preserveCart=${preserveCart})`;
      update((s) => {
        s.order.order_type = type;
        if (!preserveCart) {
          s.order.cartId = "";
        }
        clearPaymentFields(s.order);

        if (type !== "delivery") {
          s.order.userAreaName = null;
          s.order.deliveryTime = null;
          s.customer.selectedAddress = createDefaultSession().customer.selectedAddress;
        }
        return s;
      });
    },
    [update]
  );

  const setBranch = useCallback(
    (branchId: string, branchName: string, asapAllowed: number, scheduleAllowed: number, preserveCart?: boolean) => {
      sourceRef.current = `setBranch(${branchId},preserveCart=${preserveCart})`;
      update((s) => {
        s.order.branchId = branchId;
        s.order.branchName = branchName;
        s.order.branchAsapAllowed = asapAllowed;
        s.order.branchScheduleAllowed = scheduleAllowed;
        if (!preserveCart) {
          s.order.cartId = "";
        }
        if (scheduleAllowed === 0) {
          s.order.scheduledDay = null;
          s.order.scheduledPeriod = null;
        }
        return s;
      });
    },
    [update]
  );

  const setPaymentMethod = useCallback(
    (methodId: string, methodObject: Record<string, unknown>) =>
      update((s) => {
        s.order.paymentMethodId = methodId;
        s.order.paymentMethodObject = methodObject;
        return s;
      }),
    [update]
  );

  const setPaymentGateway = useCallback(
    (gateway: "myfatoorah" | "tap", method: string | null) =>
      update((s) => {
        if (gateway === "myfatoorah") {
          s.order.paymentMyfatoorah = true;
          s.order.paymentMyfatoorahMethod = method;
        } else {
          s.order.paymentTap = true;
          s.order.paymentTapMethod = method;
        }
        return s;
      }),
    [update]
  );

  const clearPayment = useCallback(
    () =>
      update((s) => {
        clearPaymentFields(s.order);
        return s;
      }),
    [update]
  );

  const setSchedule = useCallback(
    (day: string, period: string) =>
      update((s) => {
        s.order.scheduledDay = day;
        s.order.scheduledPeriod = period;
        return s;
      }),
    [update]
  );

  const clearSchedule = useCallback(
    () =>
      update((s) => {
        s.order.scheduledDay = null;
        s.order.scheduledPeriod = null;
        return s;
      }),
    [update]
  );

  const setDeliveryTime = useCallback(
    (time: string | null) =>
      update((s) => {
        s.order.deliveryTime = time;
        return s;
      }),
    [update]
  );

   const setDistance = useCallback(
    (distance: number | null) =>
      update((s) => {
        s.order.distance_km = distance;
        return s;
      }),
    [update]
  );

  const setVehicle = useCallback(
    (vehicleData: VehicleData) =>
      update((s) => {
        s.order.vehicle = vehicleData;
        return s;
      }),
    [update]
  );

  const clearVehicle = useCallback(
    () =>
      update((s) => {
        s.order.vehicle = createDefaultSession().order.vehicle;
        return s;
      }),
    [update]
  );

  const toggleUsePoints = useCallback(
    () =>
      update((s) => {
        s.order.usePoints = !s.order.usePoints;
        return s;
      }),
    [update]
  );

  const toggleUseWallet = useCallback(
    () =>
      update((s) => {
        s.order.useWallet = !s.order.useWallet;
        return s;
      }),
    [update]
  );

  const setBranchStatus = useCallback(
    (isOpen: boolean, isBusy: boolean, reason?: string | null) =>
      update((s) => {
        s.order.isOpen = isOpen;
        s.order.isBusy = isBusy;
        s.order.branchStatusReason = reason ?? null;
        return s;
      }),
    [update]
  );

  /* ---------- Customer actions ---------- */

  const loginCustomer = useCallback(
    (token: string, data: Record<string, unknown>) => {
      sourceRef.current = "loginCustomer";
      setSessionState((prev) => {
        const s = JSON.parse(JSON.stringify(prev));
        s.customer.isGuest = false;
        s.customer.token = token;
        s.customer.data = data;
        if (Array.isArray(data.addresses)) {
          s.customer.addresses = (data.addresses as AddressData[]).slice(0, MAX_ADDRESSES);
        }
        if (Array.isArray(data.vehicles)) {
          s.customer.vehicles = (data.vehicles as VehicleData[]).slice(0, MAX_VEHICLES);
        }
        // Immediately flush to localStorage so downstream callbacks
        // (e.g. place-order after auth gate) can read fresh data via getSession()
        writeSession(s, "loginCustomer-immediate");
        return s;
      });
    },
    []
  );

  const logoutCustomer = useCallback(
    () => {
      sourceRef.current = "logoutCustomer";
      update((s) => {
        const geo = { ...s.customer.geolocationCoordinates };
        s=createDefaultSession();
        // s.customer = createDefaultSession().customer;
        // s.customer.geolocationCoordinates = geo;
        // s.order.cartId = "";
        // s.order.usePoints = false;
        // s.order.useWallet = false;
        return s;
      });
    },
    [update]
  );

  const setGuestMode = useCallback(
    () =>
      update((s) => {
        s.customer.isGuest = true;
        return s;
      }),
    [update]
  );

  const updateCustomerData = useCallback(
    (data: Record<string, unknown>) =>
      update((s) => {
        s.customer.data = { ...s.customer.data, ...data };
        return s;
      }),
    [update]
  );

  const setGeolocation = useCallback(
    (lat: number, lng: number) =>
      update((s) => {
        s.customer.geolocationCoordinates = { latitude: lat, longitude: lng };
        return s;
      }),
    [update]
  );

  const setLastCoordinates = useCallback(
    (lat: number, lng: number) =>
      update((s) => {
        s.customer.lastCoordinates = { latitude: lat, longitude: lng };
        return s;
      }),
    [update]
  );

  const setSelectedAddress = useCallback(
    (address: AddressData) =>
      update((s) => {
        s.customer.selectedAddress = address;
        return s;
      }),
    [update]
  );

  const addAddress = useCallback(
    (address: AddressData) =>
      update((s) => {
        if (s.customer.addresses.length >= MAX_ADDRESSES) {
          s.customer.addresses.shift();
        }
        s.customer.addresses.push(address);
        return s;
      }),
    [update]
  );

  const updateAddress = useCallback(
    (index: number, address: AddressData) =>
      update((s) => {
        if (index >= 0 && index < s.customer.addresses.length) {
          s.customer.addresses[index] = address;
        }
        return s;
      }),
    [update]
  );

  const removeAddress = useCallback(
    (index: number) =>
      update((s) => {
        s.customer.addresses.splice(index, 1);
        return s;
      }),
    [update]
  );

  const addVehicle = useCallback(
    (vehicle: VehicleData) =>
      update((s) => {
        if (s.customer.vehicles.length >= MAX_VEHICLES) {
          s.customer.vehicles.shift();
        }
        s.customer.vehicles.push(vehicle);
        return s;
      }),
    [update]
  );

  const removeVehicle = useCallback(
    (index: number) =>
      update((s) => {
        s.customer.vehicles.splice(index, 1);
        return s;
      }),
    [update]
  );

  /* ---------- Cart actions ---------- */

  const setCartId = useCallback(
    (id: string) => {
      sourceRef.current = `setCartId(${id})`;
      update((s) => {
        s.order.cartId = id;
        return s;
      });
    },
    [update]
  );

  const clearCartId = useCallback(() => {
    sourceRef.current = "clearCartId-immediate";
    setSessionState((prev) => {
      const s = JSON.parse(JSON.stringify(prev));
      s.order.cartId = "";
      s.cartItemsLength = 0;
      // Immediately write to storage so navigation doesn't lose the cleared state
      writeSession(s, "clearCartId-immediate");
      return s;
    });
  }, []);

  const setCartItemsLength = useCallback(
    (length: number) =>
      update((s) => {
        s.cartItemsLength = length;
        return s;
      }),
    [update]
  );


  /* ---------- Lifecycle ---------- */

  const resetSession = useCallback(() => {
    sourceRef.current = "resetSession";
    const fresh = clearSession();
    setSessionState(fresh);
  }, []);

  const resetOrder = useCallback(
    () =>
      update((s) => {
        s.order = createDefaultSession().order;
        return s;
      }),
    [update]
  );

  /* ---------- Computed ---------- */

  const isGuest = session.customer.isGuest;
  const isAuthenticated = !session.customer.isGuest && !!session.customer.token;
  const orderType = session.order.order_type;
  const branchName = session.order.branchName;

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      setRestaurantId,
      setOrderType,
      setBranch,
      setPaymentMethod,
      setPaymentGateway,
      clearPayment,
      setSchedule,
      clearSchedule,
      setDeliveryTime,
      setDistance,
      setVehicle,
      clearVehicle,
      toggleUsePoints,
      toggleUseWallet,
      setBranchStatus,
      loginCustomer,
      logoutCustomer,
      setGuestMode,
      updateCustomerData,
      setGeolocation,
      setLastCoordinates,
      setSelectedAddress,
      addAddress,
      updateAddress,
      removeAddress,
      addVehicle,
      removeVehicle,
      setCartId,
      clearCartId,
      setCartItemsLength,
      resetSession,
      resetOrder,
      isGuest,
      isAuthenticated,
      orderType,
      branchName,
    }),
    [session] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSessionContext must be used within SessionProvider");
  return ctx;
}
